import { Box, Button, Typography, styled } from "@mui/material";
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";

import { ariaLabel } from "src/constants";
import { useDeviceWidth } from "src/hooks";
import { megabytesTobytes } from "src/utils";

import { UploadedDocument } from "../UploadedDocuments";

const StyledDropzone = styled("div")`
  height: 100px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${(props) => props.theme.spacing(2)};
  background-color: ${(props) => props.theme.palette.common.white};
  color: ${(props) => props.theme.palette.gray.dark};
  outline: none;
  transition: border 0.24s ease-in-out;
  &:focus {
    cursor: pointer;
  }
`;

interface Props {
  documents: Array<File> | null;
  onChange: (files: Array<File> | null) => void;
  placeholder: string;
}

export const Dropzone = ({ documents, onChange, placeholder }: Props) => {
  const onDropAccepted = (files: Array<File>) => onChange([...(documents || []), ...files]);
  const { isMobile } = useDeviceWidth();

  const disabled = documents ? documents?.length > 4 : false;

  const { getRootProps, getInputProps, isDragReject, isDragActive, isDragAccept } = useDropzone({
    accept: {
      "image/*": [],
      "application/pdf": [],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
    multiple: true,
    maxFiles: 5,
    maxSize: Math.round(megabytesTobytes(10)),
    disabled,
    onDropAccepted,
  });

  const reset = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, file: File) => {
      event.stopPropagation();

      if (documents) {onChange(documents.filter((document: File) => document.name !== file.name));}
    },
    [documents]
  );

  return (
    <Box sx={{ backgroundColor: "common.white" }}>
      <StyledDropzone
        {...getRootProps({
          isDragActive,
          isDragAccept,
          isDragReject,
        })}
        tabIndex={undefined}
        role={undefined}
      >
        <Box sx={{ color: "gray.dark", position: "absolute", left: 75 }}>
          {!isMobile && (
            <Button
              variant="outlined"
              disabled={disabled}
              aria-label={ariaLabel.browseFilesButtonLabel}
            >
              Parcourir
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex" }}>
          <input {...getInputProps()} disabled={disabled} />
          {documents && documents?.length !== 0 ? (
            documents.map((document) => (
              <UploadedDocument
                key={document.name}
                name={document.name}
                onClick={(e) => reset(e, document)}
              />
            ))
          ) : (
            <Typography sx={{ mr: 2 }}>{placeholder}</Typography>
          )}
        </Box>
      </StyledDropzone>
    </Box>
  );
};
